import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt, FaStickyNote, FaEdit, FaTimes, FaSave, FaTrash, FaEye, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { apiFetch } from '../../utils/apiAdmin';
import { adminSessionUsable } from '../../utils/authAdmin';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import DeletionProgressPane from './components/DeletionProgressPane';
import TemplateSelectionModal from './components/TemplateSelectionModal';
import CreationProgressPane from './components/CreationProgressPane';
import { useAdminWebSocket } from '../../hooks/useProjectWebSocket';

/**
 * ⛔ DEBOUNCED, AND THIS IS THE EXPENSIVE ONE (LIV-10).
 *
 * `fetchLeads` is `listLeads`: a full-table scan PLUS one `AdminGetUser` per
 * WON lead, issued in parallel. Per message, per open admin tab - and the
 * writers burst, because the won flow and the delete flow both write the leads
 * table many times over.
 *
 * ⚠️ `AdminGetUser` shares the pool's per-account rate quota with SIGN-IN, so an
 * un-debounced burst across a few open admin tabs can throttle real client
 * logins. This is the one cost in this plan that can degrade something outside
 * the admin portal.
 *
 * 500ms, matching ProjectDataContext's existing debounce.
 */
const LIVE_REFRESH_DEBOUNCE_MS = 500;

// CloneProgressModal was deleted here (PROJECT-MAILBOX T505/D15): proven dead
// three ways - selectedProjectId was only ever set to null, the backend gates
// on templateProjectId (never cloneFromProjectId), and the polled
// /admin/clone-status endpoint does not exist. The live Won flow is
// TemplateSelectionModal + CreationProgressPane.

const AdminDashboard = () => {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);
    const [editingLead, setEditingLead] = useState(null);
    // Per-project counts of issues the client has resolved, awaiting a decision
    const [awaitingApproval, setAwaitingApproval] = useState({});
    const navigate = useNavigate();
    const [addingNewLead, setAddingNewLead] = useState(false);
    const [deletingLead, setDeletingLead] = useState(null);
    // Project selection when changing status to "won"
    const [projectSelectLead, setProjectSelectLead] = useState(null);
    const [loadingProjects, setLoadingProjects] = useState(false);

    // Delete confirmation and progress tracking
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDeletionProgress, setShowDeletionProgress] = useState(false);
    const [deletionResults, setDeletionResults] = useState(null);
    const [deletionComplete, setDeletionComplete] = useState(false);

    // New client onboarding - template selection and creation progress
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showCreationProgress, setShowCreationProgress] = useState(false);
    const [creationResults, setCreationResults] = useState(null);
    const [creationComplete, setCreationComplete] = useState(false);
    // Internal onboarding only: temp password shown once in the progress pane.
    // React state ONLY - never localStorage, cleared on close and re-open.
    const [creationTempPassword, setCreationTempPassword] = useState(null);
    const [createdProjectId, setCreatedProjectId] = useState(null);
    const [isRollingBack, setIsRollingBack] = useState(false);
    const [rollbackResults, setRollbackResults] = useState(null);

    // Add lead error handling
    const [addLeadError, setAddLeadError] = useState(null);

    // A lead PUT in flight (LIV-10, gate 3). `updateLead` applies an optimistic
    // local setLeads, and a background refetch landing mid-write would fight it.
    const [savingLead, setSavingLead] = useState(false);


    const calculateStats = (leads) => {
        setStats({
            total: leads.length,
            new: leads.filter(l => l.status === 'new').length,
                 contacted: leads.filter(l => l.status === 'contacted').length,
                 qualified: leads.filter(l => l.status === 'qualified').length,
                 proposal: leads.filter(l => l.status === 'proposal').length,
                 won: leads.filter(l => l.status === 'won').length
        });
    };

    // ⚠️ SILENT MODE (LIV, D1). `{ silent: true }` is the message path: no
    // loading flag, and - the one that matters here - NO NAVIGATION. This
    // loader answers a non-ok response by sending the user to the login page,
    // which is the right thing to do behind a click and quite wrong behind a
    // signal nobody asked for.
    const fetchLeads = useCallback(async ({ silent = false } = {}) => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            if (!silent) navigate('/admin/login');
            return;
        }

        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads`);

            if (response.ok) {
                const data = await response.json();
                // Invited portal users are lead records too - they belong to
                // Client Management, not the sales pipeline
                const pipelineLeads = (data.leads || []).filter(l => !l.invitedBy);
                setLeads(pipelineLeads);
                calculateStats(pipelineLeads);
            } else if (!silent) {
                navigate('/admin/login');
            } else {
                console.warn('Background leads refresh failed - keeping the list on screen');
            }
        } catch (err) {
            console.error('Failed to fetch leads:', err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    // Counts of issues each client has resolved and that await an admin
    // decision. One call covering every project, keyed by projectId.
    const fetchAwaitingApproval = useCallback(async () => {
        // Same guard as fetchLeads: with no token this fetch went out as
        // "Bearer null", 401ed, and apiAdmin's expired-session redirect
        // bounced a fresh profile to the login page before it could ever
        // log in (found 2026-08-21).
        if (!localStorage.getItem('adminToken')) return;
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/issues/awaiting-approval`
            );
            if (response.ok) {
                const result = await response.json();
                setAwaitingApproval(result.counts || {});
            }
        } catch (err) {
            console.error('Error fetching awaiting-approval counts:', err);
        }
    }, []);

    useEffect(() => {
        fetchAwaitingApproval();
    }, [fetchAwaitingApproval]);

    // -----------------------------------------------------------------------
    // Live updates (LIV-10). The hook call stays SINGLE - the handler gains a
    // branch. Until now this page heard `issues_updated` only, so nothing about
    // a lead could live update anywhere: not a new lead, not a status change,
    // not the won conversion (F4).
    // -----------------------------------------------------------------------

    // GATE 3, the file's own flags. ⚠️ The won flow and the delete flow both
    // WRITE the leads table, so they echo back as `leads_updated` mid-flow -
    // and the progress panes are exactly when a refetch must not land.
    // `updateLead` is in there too: it applies an optimistic local setLeads
    // that a refetch would fight.
    //
    // Judged and EXCLUDED, so a later pass does not find them as an omission:
    // `selectedLead` (a read-only details panel, no typed input),
    // `projectSelectLead` (set in the same tick as showTemplateModal) and
    // `loadingProjects` (dead state - nothing sets it).
    const liveRefreshBlocked = Boolean(
        deletingLead || showDeleteConfirmation || showDeletionProgress
        || showTemplateModal || showCreationProgress || isRollingBack
        || addingNewLead || editingLead || savingLead
    );
    const liveRefreshBlockedRef = useRef(liveRefreshBlocked);
    useEffect(() => {
        liveRefreshBlockedRef.current = liveRefreshBlocked;
    }, [liveRefreshBlocked]);

    // D11: deferred, never dropped.
    const pendingRefetchRef = useRef(false);
    const refreshTimerRef = useRef(null);

    // GATE 0 lives HERE - at the point of refetch, once per coalesced refresh -
    // not per message in the socket handler. There it renewed the session for
    // messages this page ignores, and the deferred flush below reached apiFetch
    // (and its redirect) with no gate at all (Fable, round 2).
    const liveRefresh = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(async () => {
            refreshTimerRef.current = null;
            // GATE 0 - renew the session if that is all it needs; skip when it
            // cannot be renewed, so a message can never take apiAdmin's 401
            // path and redirect a tab nobody has touched.
            if (!await adminSessionUsable()) return;
            fetchLeads({ silent: true });
        }, LIVE_REFRESH_DEBOUNCE_MS);
    }, [fetchLeads]);

    useEffect(() => () => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    }, []);

    useEffect(() => {
        if (liveRefreshBlocked || !pendingRefetchRef.current) return;
        pendingRefetchRef.current = false;
        liveRefresh();
    }, [liveRefreshBlocked, liveRefresh]);

    // Cross-project channel: any project's issues changing re-reads the counts,
    // so a client resolving an issue lights up their row without a reload
    useAdminWebSocket(useCallback((message) => {
        if (message.type === 'issues_updated') {
            fetchAwaitingApproval();
            return;
        }

        // GATE 0 is inside liveRefresh, at the point of refetch.
        // GATE 1 - type. GATE 2 - none: this page lists every pipeline lead.
        if (message.type !== 'leads_updated') return;
        // GATE 3 - hold the signal rather than land on an open flow.
        if (liveRefreshBlockedRef.current) {
            pendingRefetchRef.current = true;
            return;
        }
        liveRefresh();
    }, [fetchAwaitingApproval, liveRefresh]));


    const updateLead = async (leadId, updates) => {
        setSavingLead(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads/${leadId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updates)
            });

            if (response.ok) {
                // Update local state
                setLeads(prevLeads =>
                prevLeads.map(lead =>
                lead.id === leadId ? { ...lead, ...updates } : lead
                )
                );

                // Update selected lead if it's the one being edited
                if (selectedLead?.id === leadId) {
                    setSelectedLead({ ...selectedLead, ...updates });
                }

                // Recalculate stats if status changed
                if (updates.status) {
                    const updatedLeads = leads.map(lead =>
                    lead.id === leadId ? { ...lead, ...updates } : lead
                    );
                    calculateStats(updatedLeads);
                }

                setEditingLead(null);
            } else {
                // Surface conflicts (e.g. lead already won on another session)
                // and refresh so stale rows correct themselves
                const data = await response.json().catch(() => ({}));
                alert(data.error || `Failed to update lead (${response.status})`);
                fetchLeads();
            }
        } catch (err) {
            console.error('Failed to update lead:', err);
        } finally {
            setSavingLead(false);
        }
    };

    // Handle delete button click - show confirmation modal
    const handleDeleteClick = (lead) => {
        setDeletingLead(lead);
        setShowDeleteConfirmation(true);
    };

    // Handle cancel from confirmation modal
    const handleDeleteCancel = () => {
        setShowDeleteConfirmation(false);
        setDeletingLead(null);
    };

    // Execute deletion after confirmation
    const handleDeleteLead = async (confirmCompany) => {
        // Close confirmation modal, show progress pane
        setShowDeleteConfirmation(false);
        setShowDeletionProgress(true);
        setDeletionResults(null);
        setDeletionComplete(false);

        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads/${deletingLead.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    timestamp: deletingLead.timestamp,
                    confirmCompany: confirmCompany
                })
            });

            const data = await response.json();

            setDeletionResults(data.results);
            setDeletionComplete(true);
        } catch (err) {
            console.error('Failed to delete lead:', err);
            setDeletionResults({
                cognitoUsers: { status: 'failed', count: 0, error: err.message },
                project: { status: 'skipped', count: 0, error: null },
                tasks: { status: 'skipped', count: 0, error: null },
                comments: { status: 'skipped', count: 0, error: null },
                wiki: { status: 'skipped', count: 0, error: null },
                members: { status: 'skipped', count: 0, error: null },
                risks: { status: 'skipped', count: 0, error: null },
                issues: { status: 'skipped', count: 0, error: null },
                lead: { status: 'skipped', count: 0, error: null }
            });
            setDeletionComplete(true);
        }
    };

    // Handle closing the deletion progress pane
    const handleDeletionProgressClose = () => {
        // Remove from local state if lead was successfully deleted
        if (deletionResults?.lead?.status === 'success') {
            setLeads(prevLeads => prevLeads.filter(lead => lead.id !== deletingLead.id));
            const updatedLeads = leads.filter(lead => lead.id !== deletingLead.id);
            calculateStats(updatedLeads);

            if (selectedLead?.id === deletingLead.id) {
                setSelectedLead(null);
            }
        }

        setShowDeletionProgress(false);
        setDeletingLead(null);
        setDeletionResults(null);
        setDeletionComplete(false);
    };

    // Handle status change - show template modal if changing to "won"
    const handleStatusChange = (lead, newStatus) => {
        if (newStatus === 'won' && lead.status !== 'won') {
            setProjectSelectLead(lead);
            setShowTemplateModal(true);
        } else {
            updateLead(lead.id, { status: newStatus });
        }
    };

    // Confirm project selection and update lead to "won" (blank-project path)
    const handleProjectSelect = async () => {
        if (!projectSelectLead) return;

        const updates = { status: 'won' };
        const leadId = projectSelectLead.id;

        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads/${leadId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            });

            if (response.ok) {
                const data = await response.json();

                // Update local state
                setLeads(prevLeads =>
                    prevLeads.map(lead =>
                        lead.id === leadId
                            ? { ...lead, ...updates, projectId: data.projectId }
                            : lead
                    )
                );
                calculateStats(leads.map(lead =>
                    lead.id === leadId ? { ...lead, ...updates } : lead
                ));

                setProjectSelectLead(null);
            } else {
                // SURFACE the error (e.g. 409 duplicate-onboarding guard, or
                // the mandatory project-mailbox refusal) instead of failing
                // silently
                setProjectSelectLead(null);
                const data = await response.json().catch(() => ({}));
                alert(data.error || `Failed to mark lead as won (${response.status})`);
                fetchLeads();
            }
        } catch (err) {
            console.error('Failed to update lead:', err);
        }
    };

    // Handle template selection confirmation - trigger project creation
    const handleTemplateConfirm = async (templateId, kickoffDate, internalOnboarding, projectMailbox) => {
        setShowTemplateModal(false);
        setShowCreationProgress(true);
        setCreationResults(null);
        setCreationComplete(false);
        setCreationTempPassword(null);
        setCreatedProjectId(null);
        setIsRollingBack(false);
        setRollbackResults(null);

        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads/${projectSelectLead.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: 'won',
                    templateProjectId: templateId,
                    kickoffDate: kickoffDate,
                    // Mandatory at Won (PROJECT-MAILBOX D5) - the backend
                    // refuses without it, before its first write
                    projectMailbox,
                    // Only when ticked - absent means the normal email path
                    ...(internalOnboarding ? { internalOnboarding: true } : {})
                })
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                // e.g. 409 duplicate-onboarding guard - surface it, don't feed
                // an empty steps object into the progress pane
                setShowCreationProgress(false);
                alert(data.error || `Failed to create project (${response.status})`);
                fetchLeads();
                return;
            }

            setCreationResults(data.steps);
            setCreationComplete(true);
            // Present only on the internal-onboarding success path
            setCreationTempPassword(data.tempPassword);
            setCreatedProjectId(data.projectId);

            if (data.success) {
                // Update lead in list
                setLeads(prev => prev.map(l =>
                    l.id === projectSelectLead.id
                        ? { ...l, status: 'won', projectId: data.projectId }
                        : l
                ));
                calculateStats(leads.map(lead =>
                    lead.id === projectSelectLead.id ? { ...lead, status: 'won' } : lead
                ));
            }
        } catch (error) {
            setCreationResults({
                project: { status: 'failed', count: 0, error: error.message },
                tasks: { status: 'skipped', count: 0, error: null },
                wiki: { status: 'skipped', count: 0, error: null },
                cognitoUser: { status: 'skipped', count: 0, error: null },
                email: { status: 'skipped', count: 0, error: null },
                // Every CREATION_STEPS key is named here: the pane defaults a
                // missing one to 'pending', which spins for ever on a request
                // that already failed.
                roster: { status: 'skipped', count: 0, error: null }
            });
            setCreationComplete(true);
        }
    };

    // Handle rollback of failed project creation
    const handleRollback = async () => {
        setIsRollingBack(true);
        setRollbackResults(null);

        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads/${projectSelectLead.id}/rollback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ projectId: createdProjectId })
            });

            const data = await response.json();
            setRollbackResults(data.steps);
        } catch (error) {
            setRollbackResults({
                cognitoUser: { status: 'failed', count: 0, error: error.message },
                wiki: { status: 'skipped', count: 0, error: null },
                tasks: { status: 'skipped', count: 0, error: null },
                project: { status: 'skipped', count: 0, error: null }
            });
        }

        setIsRollingBack(false);
    };

    // Handle closing the creation progress pane
    const handleCreationProgressClose = () => {
        setShowCreationProgress(false);
        setProjectSelectLead(null);
        setCreationResults(null);
        setCreationComplete(false);
        setCreationTempPassword(null);
        setCreatedProjectId(null);
        setIsRollingBack(false);
        setRollbackResults(null);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setAddLeadError(null);

        // Project details: EITHER the complete migration trio OR the Other
        // description - never neither (the portal would have no description)
        const trioComplete = editingLead.sourceTenant?.trim() && editingLead.targetTenant?.trim() && editingLead.numberOfUsers?.trim();
        if (!trioComplete && !editingLead.projectDescription?.trim()) {
            setAddLeadError('Provide the migration details (source, target, users) or the Other description');
            return;
        }

        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/leads`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...editingLead,
                    status: 'new',
                    createdAt: new Date().toISOString()
                })
            });

            if (response.ok) {
                const newLead = await response.json();
                setLeads([newLead, ...leads]);
                calculateStats([newLead, ...leads]);
                setAddingNewLead(false);
                setEditingLead(null);
                setAddLeadError(null);
            } else {
                const errorData = await response.json();
                setAddLeadError(errorData.error || 'Failed to add client');
            }
        } catch (err) {
            console.error('Failed to add lead:', err);
            setAddLeadError('Failed to add client: ' + err.message);
        }
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();
        if (editingLead) {
            // Same either/or rule as add: complete trio OR Other description.
            // Surfaced via alert - the channel every edit-path error already uses
            const trioComplete = editingLead.sourceTenant?.trim() && editingLead.targetTenant?.trim() && editingLead.numberOfUsers?.trim();
            if (!trioComplete && !editingLead.projectDescription?.trim()) {
                alert('Provide the migration details (source, target, users) or the Other description');
                return;
            }
            if (editingLead.status === 'won') {
                // The flag is frozen server-side on won leads; this wholesale
                // spread must not carry it or every won-lead edit would trip
                // the change check
                const { internalOnboarding, ...rest } = editingLead;
                updateLead(editingLead.id, rest);
            } else {
                updateLead(editingLead.id, editingLead);
            }
        }
    };

    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-gray-600">Loading Client Portal...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
        <div className="container-width py-8">
        {/* Header */}
        <div className="mb-8">
        <h1 className="text-3xl font-light text-primary-900">
        Client <span className="font-normal">Dashboard</span>
        </h1>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-light text-primary-700">{stats.total}</div>
        <div className="text-gray-600 text-sm">Total</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-light text-green-600">{stats.new}</div>
        <div className="text-gray-600 text-sm">New</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-light text-yellow-600">{stats.contacted}</div>
        <div className="text-gray-600 text-sm">Contacted</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-light text-blue-600">{stats.qualified}</div>
        <div className="text-gray-600 text-sm">Qualified</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-light text-purple-600">{stats.proposal}</div>
        <div className="text-gray-600 text-sm">Proposal</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-light text-emerald-600">{stats.won}</div>
        <div className="text-gray-600 text-sm">Won</div>
        </div>
        </div>

        {/* Main Content Area */}
        <div className="grid lg:grid-cols-3 gap-6">
        {/* Leads List */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
        <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-primary-800">Client Leads</h2>
        <button
        onClick={() => {
            setEditingLead({
                name: '',
                email: '',
                company: '',
                phone: '',
                address: '',
                city: '',
                postcode: '',
                mainContactName: '',
                initialContactDate: '',
                sourceTenant: '',
                targetTenant: '',
                numberOfUsers: '',
                projectDescription: '',
                notes: ''
            });
            setAddingNewLead(true);
        }}
        className="btn-primary text-sm"
        >
        + Add New Client
        </button>
        </div>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full">
        <thead className="bg-gray-50 border-b">
        <tr>
        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-10" title="Client has logged in"></th>
        </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
        {leads.map(lead => (
            <tr
            key={lead.id}
            className={`cursor-pointer transition-colors ${
                selectedLead?.id === lead.id
                ? 'bg-primary-50 hover:bg-primary-100 border-l-4 border-primary-500'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => setSelectedLead(lead)}
            >
            <td className="px-4 py-3 text-sm">
            <div className="font-medium">{lead.name}</div>
            <div className="text-gray-500 text-xs">{lead.email}</div>
            </td>
            <td className="px-4 py-3 text-sm">{lead.company}</td>
            <td className="px-4 py-3">
            <select
            value={lead.status}
            disabled={lead.status === 'won'}
            title={lead.status === 'won' ? 'Won is locked - the client has a live project. Delete the lead to remove the client entirely.' : undefined}
            onChange={(e) => {
                e.stopPropagation();
                handleStatusChange(lead, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            className={`text-sm border rounded px-2 py-1 ${lead.status === 'won' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            >
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="proposal">Proposal</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            </select>
            </td>
            <td className="px-4 py-3 text-sm">
            <div className="flex gap-2">
            {lead.status === 'won' && (
                <button
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/client-view/${lead.id}`);
                }}
                className="relative text-green-600 hover:text-green-800"
                title={awaitingApproval[lead.projectId]
                    ? `View client dashboard - ${awaitingApproval[lead.projectId]} issue(s) awaiting your approval`
                    : 'View client dashboard'}
                >
                <FaEye />
                {awaitingApproval[lead.projectId] > 0 && (
                    <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-teal-600 text-white text-[10px] font-semibold leading-none">
                        {awaitingApproval[lead.projectId]}
                    </span>
                )}
                </button>
            )}
            <button
            onClick={(e) => {
                e.stopPropagation();
                setEditingLead(lead);
            }}
            className="text-primary-600 hover:text-primary-800"
            >
            <FaEdit />
            </button>
            <button
            onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(lead);
            }}
            className="text-red-400 hover:text-red-800"
            >
            <FaTrash />
            </button>
            </div>
            </td>
            <td className="px-4 py-3 text-center">
            {lead.firstLoginAt && (
                <span className="text-green-500" title={`Client logged in: ${new Date(lead.firstLoginAt).toLocaleDateString('en-GB')}`}>
                <FaCheck />
                </span>
            )}
            </td>
            </tr>
        ))}
        </tbody>
        </table>
        </div>
        </div>

        {/* Client Details Panel */}
        <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
        <h2 className="text-lg font-medium text-primary-800">Client Details</h2>
        {selectedLead && (
            <button
            onClick={() => setEditingLead(selectedLead)}
            className="text-primary-600 hover:text-primary-800"
            >
            <FaEdit />
            </button>
        )}
        </div>

        {selectedLead ? (
            <div className="p-4 space-y-4">
            {/* Basic Info */}
            <div className="space-y-3">
            <div className="flex items-start gap-3">
            <FaUser className="mt-1 text-gray-400" />
            <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Contact Name</p>
            <p className="text-sm text-gray-900">{selectedLead.name}</p>
            </div>
            </div>

            <div className="flex items-start gap-3">
            <FaBuilding className="mt-1 text-gray-400" />
            <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Company</p>
            <p className="text-sm text-gray-900">{selectedLead.company}</p>
            </div>
            </div>

            <div className="flex items-start gap-3">
            <FaEnvelope className="mt-1 text-gray-400" />
            <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Email</p>
            <p className="text-sm text-gray-900">{selectedLead.email}</p>
            </div>
            </div>

            <div className="flex items-start gap-3">
            <div className="flex-1 ml-7">
            <p className="text-sm font-medium text-gray-700">Main Contact</p>
            <p className="text-sm text-gray-900">{selectedLead.mainContactName || 'TBC'}</p>
            </div>
            </div>

            <div className="flex items-start gap-3">
            <FaPhone className="mt-1 text-gray-400" />
            <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Phone</p>
            <p className="text-sm text-gray-900">{selectedLead.phone || 'TBC'}</p>
            </div>
            </div>

            <div className="flex items-start gap-3">
            <div className="flex-1 ml-7">
            <p className="text-sm font-medium text-gray-700">Initial Contact Date</p>
            <p className="text-sm text-gray-900">
            {selectedLead.initialContactDate
                ? new Date(selectedLead.initialContactDate).toLocaleDateString('en-GB')
                : selectedLead.createdAt
                ? new Date(selectedLead.createdAt).toLocaleDateString('en-GB')
                : 'TBC'}
                </p>
                </div>
                </div>

                <div className="flex items-start gap-3">
                <FaMapMarkerAlt className="mt-1 text-gray-400" />
                <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">Address</p>
                <p className="text-sm text-gray-900">
                {selectedLead.address || 'TBC'}
                {selectedLead.city && <><br />{selectedLead.city}</>}
                {selectedLead.postcode && <>, {selectedLead.postcode}</>}
                </p>
                </div>
                </div>
                </div>

                {/* Project Details */}
                <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Project Details</h3>
                <div className="space-y-2 text-sm">
                <p><span className="font-medium">From:</span> {selectedLead.sourceTenant || 'TBC'}</p>
                <p><span className="font-medium">To:</span> {selectedLead.targetTenant || 'TBC'}</p>
                <p><span className="font-medium">Users:</span> {selectedLead.numberOfUsers || 'TBC'}</p>
                <p><span className="font-medium">Other:</span> {selectedLead.projectDescription || 'TBC'}</p>
                </div>
                </div>

                {/* Notes */}
                <div className="border-t pt-4">
                <div className="flex items-start gap-3">
                <FaStickyNote className="mt-1 text-gray-400" />
                <div className="flex-1">
                <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {selectedLead.notes || 'No notes added'}
                </p>
                </div>
                </div>
                </div>
                </div>
        ) : (
            <div className="p-8 text-center text-gray-500">
            Select a customer to view details
            </div>
        )}
        </div>
        </div>
        </div>

        {/* Edit Modal */}
        {editingLead && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-xl font-medium">Edit Client Details</h2>
            <button onClick={() => setEditingLead(null)} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
            </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
            type="text"
            value={editingLead.name}
            onChange={(e) => setEditingLead({...editingLead, name: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input
            type="text"
            value={editingLead.company}
            onChange={(e) => setEditingLead({...editingLead, company: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
            type="email"
            value={editingLead.email}
            onChange={(e) => setEditingLead({...editingLead, email: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Contact</label>
            <input
            type="text"
            value={editingLead.mainContactName || ''}
            onChange={(e) => setEditingLead({...editingLead, mainContactName: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Name of main contact"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
            type="tel"
            value={editingLead.phone || ''}
            onChange={(e) => setEditingLead({...editingLead, phone: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Enter phone number"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Contact Date</label>
            <input
            type="date"
            value={
                editingLead.initialContactDate
                ? editingLead.initialContactDate.split('T')[0]
                : editingLead.createdAt
                ? editingLead.createdAt.split('T')[0]
                : ''
            }
            onChange={(e) => setEditingLead({...editingLead, initialContactDate: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            </div>

            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
            type="text"
            value={editingLead.address || ''}
            onChange={(e) => setEditingLead({...editingLead, address: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Street address"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
            type="text"
            value={editingLead.city || ''}
            onChange={(e) => setEditingLead({...editingLead, city: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="City"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input
            type="text"
            value={editingLead.postcode || ''}
            onChange={(e) => setEditingLead({...editingLead, postcode: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Postcode"
            />
            </div>

            <div className="md:col-span-2">
            <h3 className="text-sm font-medium text-gray-700 mb-2 mt-2">Project Details</h3>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Tenant</label>
            <select
            value={editingLead.sourceTenant || ''}
            onChange={(e) => setEditingLead({...editingLead, sourceTenant: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
            <option value="">Select source</option>
            <option value="Google Cloud">Google Cloud</option>
            <option value="Microsoft 365">Microsoft 365</option>
            <option value="Other">Other</option>
            </select>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Tenant</label>
            <select
            value={editingLead.targetTenant || ''}
            onChange={(e) => setEditingLead({...editingLead, targetTenant: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
            <option value="">Select target</option>
            <option value="Google Cloud">Google Cloud</option>
            <option value="Microsoft 365">Microsoft 365</option>
            <option value="Other">Other</option>
            </select>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number of Users</label>
            <select
            value={editingLead.numberOfUsers || ''}
            onChange={(e) => setEditingLead({...editingLead, numberOfUsers: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
            <option value="">Select range</option>
            <option value="1-100">1 - 100</option>
            <option value="100-250">100 - 250</option>
            <option value="250-500">250 - 500</option>
            <option value="500-1000">500 - 1000</option>
            <option value="1000+">1000+</option>
            </select>
            </div>

            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Other</label>
            <input
            type="text"
            value={editingLead.projectDescription || ''}
            onChange={(e) => setEditingLead({...editingLead, projectDescription: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Project description"
            maxLength={200}
            />
            </div>

            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
            value={editingLead.notes || ''}
            onChange={(e) => setEditingLead({...editingLead, notes: e.target.value})}
            rows="4"
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Add any notes about this customer..."
            />
            </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
            <button
            type="button"
            onClick={() => setEditingLead(null)}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
            Cancel
            </button>
            <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            >
            <FaSave /> Save Changes
            </button>
            </div>
            </form>
            </div>
            </div>
        )}

        {/* Add New Client Modal */}
        {addingNewLead && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-xl font-medium">Add New Client</h2>
            <button onClick={() => {setAddingNewLead(false); setEditingLead(null); setAddLeadError(null);}} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
            </button>
            </div>

            <form onSubmit={handleAddSubmit} className="p-6 space-y-4">
            {addLeadError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                    <FaExclamationTriangle className="flex-shrink-0" />
                    {addLeadError}
                </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
            type="text"
            value={editingLead?.name || ''}
            onChange={(e) => setEditingLead({...editingLead, name: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            required
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
            <input
            type="text"
            value={editingLead?.company || ''}
            onChange={(e) => setEditingLead({...editingLead, company: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            required
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
            type="email"
            value={editingLead?.email || ''}
            onChange={(e) => setEditingLead({...editingLead, email: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            required
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Contact</label>
            <input
            type="text"
            value={editingLead?.mainContactName || ''}
            onChange={(e) => setEditingLead({...editingLead, mainContactName: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Name of main contact"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
            type="tel"
            value={editingLead?.phone || ''}
            onChange={(e) => setEditingLead({...editingLead, phone: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Enter phone number"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initial Contact Date</label>
            <input
            type="date"
            value={editingLead?.initialContactDate ? editingLead.initialContactDate.split('T')[0] : ''}
            onChange={(e) => setEditingLead({...editingLead, initialContactDate: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            </div>

            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
            type="text"
            value={editingLead?.address || ''}
            onChange={(e) => setEditingLead({...editingLead, address: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Street address"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
            type="text"
            value={editingLead?.city || ''}
            onChange={(e) => setEditingLead({...editingLead, city: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="City"
            />
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input
            type="text"
            value={editingLead?.postcode || ''}
            onChange={(e) => setEditingLead({...editingLead, postcode: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Postcode"
            />
            </div>

            <div className="md:col-span-2">
            <h3 className="text-sm font-medium text-gray-700 mb-2 mt-2">Project Details</h3>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Tenant</label>
            <select
            value={editingLead.sourceTenant || ''}
            onChange={(e) => setEditingLead({...editingLead, sourceTenant: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
            <option value="">Select source</option>
            <option value="Google Cloud">Google Cloud</option>
            <option value="Microsoft 365">Microsoft 365</option>
            <option value="Other">Other</option>
            </select>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Tenant</label>
            <select
            value={editingLead.targetTenant || ''}
            onChange={(e) => setEditingLead({...editingLead, targetTenant: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
            <option value="">Select target</option>
            <option value="Google Cloud">Google Cloud</option>
            <option value="Microsoft 365">Microsoft 365</option>
            <option value="Other">Other</option>
            </select>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number of Users</label>
            <select
            value={editingLead.numberOfUsers || ''}
            onChange={(e) => setEditingLead({...editingLead, numberOfUsers: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
            <option value="">Select range</option>
            <option value="1-100">1 - 100</option>
            <option value="100-250">100 - 250</option>
            <option value="250-500">250 - 500</option>
            <option value="500-1000">500 - 1000</option>
            <option value="1000+">1000+</option>
            </select>
            </div>

            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Other</label>
            <input
            type="text"
            value={editingLead?.projectDescription || ''}
            onChange={(e) => setEditingLead({...editingLead, projectDescription: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Project description"
            maxLength={200}
            />
            </div>

            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
            value={editingLead?.notes || ''}
            onChange={(e) => setEditingLead({...editingLead, notes: e.target.value})}
            rows="4"
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Add any notes about this client..."
            />
            </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
            <button
            type="button"
            onClick={() => {setAddingNewLead(false); setEditingLead(null); setAddLeadError(null);}}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
            Cancel
            </button>
            <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            >
            <FaSave /> Add Client
            </button>
            </div>
            </form>
            </div>
            </div>
        )}

        {/* Project Selection Modal - shown when changing status to "won" */}
        {projectSelectLead && (
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
            <h2 className="text-xl font-medium text-gray-900">Create Project</h2>
            <p className="text-sm text-gray-500 mt-1">
            Setting <span className="font-semibold">{projectSelectLead.company}</span> to Won
            </p>
            </div>

            <div className="p-6 space-y-4">
            <p className="text-gray-700">
            A new project will be created for this client. They will receive login credentials via email.
            </p>

            <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
            <div>
            <p className="font-medium text-gray-900">Create blank project</p>
            <p className="text-sm text-gray-500">A new empty project will be created</p>
            </div>
            </div>
            </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
            <button
            onClick={() => setProjectSelectLead(null)}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
            Cancel
            </button>
            <button
            onClick={handleProjectSelect}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
            Create Project
            </button>
            </div>
            </div>
            </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
            isOpen={showDeleteConfirmation}
            lead={deletingLead}
            onConfirm={handleDeleteLead}
            onCancel={handleDeleteCancel}
        />

        {/* Deletion Progress Pane */}
        <DeletionProgressPane
            isOpen={showDeletionProgress}
            clientName={deletingLead?.company}
            results={deletionResults}
            isComplete={deletionComplete}
            onClose={handleDeletionProgressClose}
        />

        {/* Template Selection Modal - New client onboarding */}
        <TemplateSelectionModal
            isOpen={showTemplateModal}
            lead={projectSelectLead}
            onClose={() => {
                setShowTemplateModal(false);
                setProjectSelectLead(null);
            }}
            onConfirm={handleTemplateConfirm}
        />

        {/* Creation Progress Pane - New client onboarding */}
        <CreationProgressPane
            isOpen={showCreationProgress}
            results={creationResults}
            isComplete={creationComplete}
            tempPassword={creationTempPassword}
            hasErrors={creationResults && Object.values(creationResults).some(s => s.status === 'failed')}
            clientName={projectSelectLead?.company}
            isRollingBack={isRollingBack}
            rollbackResults={rollbackResults}
            onRollback={handleRollback}
            onClose={handleCreationProgressClose}
        />
        </div>
    );
};

export default AdminDashboard;

